import { useEffect, useRef } from 'react'

// Lightweight custom particle renderer — no external deps.
// Tuned to mimic the legacy `react-particles-js` behavior:
// - links only when particles are close enough
// - faster movement
function createParticles(width, height, count) {
  return Array.from({ length: count }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * 2.6,
    vy: (Math.random() - 0.5) * 2.6,
    r: 1 + Math.random() * 2.2,
  }))
}

const BASE_LINK_DIST = 150
const REF_SIZE = 1000 // reference min-dimension for BASE_LINK_DIST
const COLOR = '130, 130, 130'

function linkDistForSize(w, h) {
  const scale = Math.min(w, h) / REF_SIZE
  return Math.max(40, BASE_LINK_DIST * scale)
}

export default function ParticlesBackground() {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    let particles = []
    let raf = 0
    let last = performance.now()
    let linkDist = BASE_LINK_DIST
    let linkDistSq = linkDist * linkDist

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect()
      const w = Math.max(1, rect.width)
      const h = Math.max(1, rect.height)
      canvas.width = w
      canvas.height = h
      linkDist = linkDistForSize(w, h)
      linkDistSq = linkDist * linkDist
      particles = createParticles(w, h, 95)
      last = performance.now()
    }

    const draw = (now) => {
      const w = canvas.width
      const h = canvas.height
      ctx.clearRect(0, 0, w, h)

      const dt = Math.min(2, (now - last) / 16.67)
      last = now

      // move (faster)
      for (const p of particles) {
        p.x += p.vx * dt
        p.y += p.vy * dt

        if (p.x < 0) p.x = w
        if (p.x > w) p.x = 0
        if (p.y < 0) p.y = h
        if (p.y > h) p.y = 0
      }

      // draw links only when close
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const distSq = dx * dx + dy * dy
          if (distSq < linkDistSq) {
            const alpha = (1 - distSq / linkDistSq) * 0.4
            ctx.beginPath()
            ctx.strokeStyle = `rgba(${COLOR},${alpha})`
            ctx.lineWidth = 1
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)
            ctx.stroke()
          }
        }
      }

      // draw dots
      for (const p of particles) {
        ctx.beginPath()
        ctx.fillStyle = `rgba(${COLOR},0.5)`
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fill()
      }

      raf = requestAnimationFrame(draw)
    }

    resize()
    raf = requestAnimationFrame(draw)
    window.addEventListener('resize', resize)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  )
}
